if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.button = this.querySelector('.load-more');
      this.grid = Array.from(
        document.querySelectorAll('[data-id]')
      ).find(element => element.dataset.id === this.dataset.section);

      this.nextUrl = this.dataset.nextUrl || '';
      this.busy = false;
      this.failed = false;
      this.abortController = null;

      if (!this.grid) return;

      this.button?.addEventListener('click', event => {
        event.preventDefault();
        this.failed = false;
        this.loadProducts();
      });

      if (this.classList.contains('pagination-type--infinite')) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.loadProducts();
          }
        }, {
          rootMargin: '0px 0px 500px 0px',
          threshold: 0
        });

        this.observer.observe(this);
      }

      if (!this.nextUrl) this.finish();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abortController?.abort();
      this.initialized = false;
    }

    finish() {
      this.nextUrl = '';
      this.observer?.disconnect();
      this.hidden = true;
      this.removeAttribute('loading');
    }

    async loadProducts() {
      if (this.busy || this.failed || !this.nextUrl) return;

      this.busy = true;
      this.setAttribute('loading', 'true');
      if (this.button) this.button.disabled = true;

      const url = this.nextUrl;
      this.abortController = new AbortController();
      const timeout = setTimeout(
        () => this.abortController.abort(), 20000
      );

      let succeeded = false;

      try {
        const response = await fetch(url, {
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }

        const html = await response.text();
        if (!this.isConnected) return;

        const doc = new DOMParser().parseFromString(
          html, 'text/html'
        );
        const fetchedGrid = doc.getElementById('product-grid');

        if (!fetchedGrid) {
          throw new Error('Product grid missing from response');
        }

        const products = Array.from(
          fetchedGrid.querySelectorAll('.column')
        );

        const pagination = Array.from(
          doc.querySelectorAll('pagination-theme')
        ).find(element =>
          element.dataset.section === this.dataset.section
        );

        const followingUrl = pagination?.dataset.nextUrl || '';

        if (followingUrl && followingUrl === url) {
          throw new Error('Pagination did not advance');
        }

        if (!products.length) {
          if (followingUrl) {
            throw new Error('Empty product page');
          }
          this.finish();
          return;
        }

        const fragment = document.createDocumentFragment();
        products.forEach(product => fragment.appendChild(product));
        this.grid.appendChild(fragment);

        // Avancer seulement après l'ajout réussi.
        this.nextUrl = followingUrl;
        succeeded = true;
        this.failed = false;

        if (this.button) {
          this.button.style.removeProperty('display');
        }

        if (!this.nextUrl) this.finish();

        // Les intégrations ne doivent pas bloquer la pagination.
        try {
          products.forEach(product => {
            product.querySelectorAll('.lazyload').forEach(image => {
              if (window.lazySizes?.loader) {
                window.lazySizes.loader.unveil(image);
              }
            });
          });
        } catch (error) {
          console.warn('Pagination images:', error);
        }

        try {
          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('pagination:page-changed', { url });
          }
        } catch (error) {
          console.warn('Pagination event:', error);
        }

        setTimeout(() => {
          try {
            if (typeof iWishQV !== 'undefined') {
              iWishQV.init();
            }
          } catch (error) {
            console.warn('Pagination wishlist:', error);
          }
        }, 1500);

      } catch (error) {
        if (!this.isConnected) return;

        console.error('Pagination:', error);
        this.failed = true;

        // En mode infini, rendre le bouton disponible pour réessayer.
        if (this.button) {
          this.button.style.setProperty('display', 'inline-flex');
        }
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        this.removeAttribute('loading');
        if (this.button) this.button.disabled = false;

        // Continuer si la pagination reste près de l'écran,
        // sans exiger de remonter puis redescendre.
        if (
          succeeded &&
          this.nextUrl &&
          this.isConnected &&
          this.classList.contains('pagination-type--infinite')
        ) {
          requestAnimationFrame(() => {
            const rect = this.getBoundingClientRect();
            if (
              rect.top <= window.innerHeight + 500 &&
              rect.bottom >= 0
            ) {
              this.loadProducts();
            }
          });
        }
      }
    }
  }

  customElements.define('pagination-theme', Pagination);
}
